import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FieldWrapper } from "./fields";
import { Row, Col, Modal, Alert } from "react-bootstrap";
import { LinkButton, Button } from "../../basic/Button";
import { useDebounce } from "@reactuses/core";
import { css } from "../../../../../styled-system/css";
import { SearchInput } from "../../../fields/base/TextField";
import { ButtonGroup } from "../../basic/ButtonGroup";
import { StringListInput } from "../../../fields/base/StringListInput";
import TagsReviewDisplay from "../TagsReviewDisplay";
import { HoverInfo } from "../../basic/Tooltip";

const AttributeSelector = ({ state, errors, fieldsToValidate, onChange, funnelTargeting, review, isModal }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const attributeDropDown = [
    { value: "color", label: t(($) => $.advertising["Color"]) },
    { value: "size", label: t(($) => $.advertising["Size"]) },
    { value: "collection", label: t(($) => $.advertising["Collection"]) },
    { value: "material", label: t(($) => $.advertising["Material"]) },
    { value: "flavor", label: t(($) => $.advertising["Flavor"]) },
    { value: "other", label: t(($) => $.advertising["Other"]) },
  ];

  const [showFields, setShowFields] = useState(false);
  const [discoveryKeywords] = useState([]);
  const [filteredKeywords, setFilteredKeywords] = useState([]);
  const [keywordSearch, setKeywordSearch] = useState("");
  const debounceSearch = useDebounce(keywordSearch, 1000);
  const [selectedAttribute, setSelectedAttribute] = useState(attributeDropDown[0].value);
  const [attributeData, setAttributeData] = useState({
    color: [],
    size: [],
    collection: [],
    flavor: [],
    material: [],
    other: [],
  });
  const [productTypes, setProductType] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [adId, setAdId] = useState(null);

  // Modal State
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  const addProductKeyword = (kw) => {
    const productKeys = state.filledProductKeys;
    productKeys.push(kw);

    onChange({ filledProductKeys: productKeys });
  };

  const getAttributes = async () => {
    const partialLocalState = {
      filledProductKeys: state.filledProductKeys,
      filledProductBrands: state.filledProductBrands,
      filledCompetitorBrands: state.filledCompetitorBrands,
      filteredAttributes: state.filteredAttributes,
    };

    setAttributeData(state.filteredAttributes);
    setProductType(state.filledProductKeys);
    setBrands(state.filledProductBrands);

    onChange(partialLocalState);
  };

  const checkAttributeInPrdType = (attributes, productTypes) => {
    let ptMono = [];
    productTypes.forEach((p) => {
      ptMono = ptMono.concat(p.split(" "));
    });
    ptMono = [...new Set(ptMono)];

    for (let i = 0; i < Object.keys(attributes).length; i++) {
      const k = Object.keys(attributes)[i];
      let values = [];
      attributes[k].forEach((v) => {
        values = values.concat(v.toLowerCase().split(" "));
      });
      values = [...new Set(values)];

      values.forEach(() => {});

      const both = [...new Set([...ptMono, ...values])];
      if (both.length < values.length + ptMono.length) {
        return false;
      }
    }

    return true;
  };

  const changeProductType = (pt) => {
    let pTypes = [];
    pt.forEach((x) => pTypes.push(x.toLowerCase()));
    pTypes = [...new Set(pTypes)];

    setProductType(pTypes);
    onChange({ filledProductKeys: pTypes });

    if (!checkAttributeInPrdType(attributeData, pTypes)) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const changeBrand = (brd) => {
    let Brds = [];
    brd.forEach((x) => Brds.push(x.toLowerCase()));
    Brds = [...new Set(Brds)];

    const partialState = {
      filledProductBrands: Brds,
    };

    setBrands(Brds);
    onChange(partialState);

    if (!checkAttributeInPrdType(attributeData, Brds)) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const changeCompetitorBrand = (brandList) => {
    let competitorBrands = [];
    brandList.forEach((brand) => competitorBrands.push(brand.toLowerCase()));
    competitorBrands = [...new Set(competitorBrands)];

    const partialState = {
      filledCompetitorBrands: competitorBrands,
    };

    onChange(partialState);
  };

  const changeAttributeData = (attr, v) => {
    const attrData = { ...attributeData };
    attrData[attr] = v;

    setAttributeData(attrData);
    onChange({ filteredAttributes: attrData });

    if (!checkAttributeInPrdType(attrData, productTypes) || !checkAttributeInPrdType(attrData, brands)) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  useEffect(() => {
    if (
      ((state.product && state.product.length > 0) ||
        state.creativeProduct?.length ||
        state.landingPageProduct?.length ||
        state.storeSpotlight ||
        state.manualCollection ||
        state.autoCollection) &&
      funnelTargeting
    ) {
      setShowFields(true);

      getAttributes(state.product);
    } else {
      setShowFields(false);
    }

    setAdId(state.id);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [state.product, state.manualCollection, state.autoCollection, funnelTargeting]);

  useEffect(() => {
    if (!discoveryKeywords) {
      return;
    }

    if (keywordSearch.length === 0) {
      setFilteredKeywords(discoveryKeywords);
    }

    setFilteredKeywords(discoveryKeywords.filter((kw) => kw.indexOf(keywordSearch) > -1));

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearch]);

  if (!funnelTargeting) {
    return <div></div>;
  }
  if (!showFields) return <div></div>;

  const getCompetitorBrands = () => {
    return (
      <Col xs={12} lg={6} style={{ paddingTop: "1rem", paddingBottom: "1rem" }}>
        <FieldWrapper>
          {review ?
            <TagsReviewDisplay
              tags={state.filledCompetitorBrands}
              label={t(($) => $.advertising["Competitor Brands"])}
              toolTip={t(($) => $.advertising["Tell us the brands you are competing against in your media plan"])}
            />
          : <StringListInput
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  {t(($) => $.advertising["Competitor Brands"])}
                  <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                    {t(($) => $.advertising["Tell us the brands you are competing against in your media plan"])}
                  </HoverInfo>
                </span>
              }
              value={state.filledCompetitorBrands ?? []}
              onChange={(b) => changeCompetitorBrand(b)}
              placeholder={t(($) => $.advertising["Enter competitor brands..."])}
              isDisabled={(adId || review) && !isModal}
              bulkEditTitle={t(($) => $.advertising["Competitor Brands"])}
            />
          }
          <Row hidden={review}>
            <div
              style={{
                paddingTop: 0,
                fontSize: "12px",
                paddingLeft: "1.6rem",
              }}
            >
              <span>{t(($) => $.advertising["e.g. Nike, Apple, Dell"])}</span>
            </div>
            {errors.filledCompetitorBrands && fieldsToValidate?.includes("filledCompetitorBrands") && (
              <small className="text-danger pt-2 ps-4">{errors.filledCompetitorBrands}</small>
            )}
            {errors.brandTargetCollision && fieldsToValidate?.includes("brandTargetCollision") && (
              <small className="text-danger pt-2 ps-4">{errors.brandTargetCollision}</small>
            )}
          </Row>
        </FieldWrapper>
      </Col>
    );
  };

  const getProductTypes = () => {
    return (
      <Col
        xs={12}
        lg={state.canHaveChildren ? 12 : 6}
        hidden={review && state.filledProductKeys?.length === 0}
        style={{ paddingTop: 0 }}
      >
        <FieldWrapper>
          <div style={{ overflow: "auto" }}>
            {review ?
              <TagsReviewDisplay
                tags={state.filledProductKeys}
                label={t(($) => $.advertising["Product Type"])}
                toolTip={t(($) => $.advertising["Tell us the keywords that describe your product best"])}
              />
            : <StringListInput
                label={
                  <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                    {t(($) => $.advertising["Product Type"])}
                    <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                      {t(($) => $.advertising["Tell us the keywords that describe your product best"])}
                    </HoverInfo>
                  </span>
                }
                value={state.filledProductKeys ?? []}
                onChange={(pt) => changeProductType(pt)}
                placeholder={t(($) => $.advertising["Enter product types..."])}
                isDisabled={(adId || review) && !isModal}
                bulkEditTitle={t(($) => $.advertising["Product Types"])}
              />
            }
          </div>
          <Row hidden={review}>
            <div style={{ paddingTop: 0, paddingLeft: "1.6rem" }}>
              <span style={{ fontSize: "12px" }}>{t(($) => $.advertising["e.g. Coffee Cup, Coffee Mug"])}</span>
            </div>
          </Row>
          {errors.filledProductKeys && fieldsToValidate?.includes("filledProductKeys") && (
            <small className="text-danger pt-3">{errors.filledProductKeys}</small>
          )}
        </FieldWrapper>
      </Col>
    );
  };

  const getAttributeTitle = () => {
    return (
      <Row>
        <Col
          xs={12}
          style={{
            padding:
              review ? "2rem .7rem 1rem"
              : state.canHaveChildren ? "0 .8rem"
              : "0 2rem",
            marginTop: "-1rem",
          }}
          hidden={!review}
        >
          <h6
            className={css({
              color: "text.label",
              fontWeight: "700",
              padding: "2rem 0 1rem",
              textTransform: "none",
              display: "flex",
            })}
            hidden={Object.keys(attributeData).every((key) => attributeData[key]?.length === 0)}
          >
            {t(($) => $.advertising["Product Attributes"])}
            <HoverInfo iconCss={{ marginLeft: "small", color: "grey", fontSize: "small" }}>
              {t(($) => $.advertising["Give us a list of the attributes that describe your products"])}
            </HoverInfo>
          </h6>
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {attributeDropDown.map((attribute) => {
              if (!attributeData[attribute.value] || attributeData[attribute.value]?.length < 1) {
                return null;
              }
              return (
                <div key={attribute.value} style={{ flexDirection: "column", display: "flex" }}>
                  <span
                    style={{
                      fontSize: "1.35rem",
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      color: "#73706E",
                    }}
                  >
                    {attribute.label}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      marginBottom: "1rem",
                    }}
                  >
                    {review ?
                      <TagsReviewDisplay tags={attributeData[attribute.value]} />
                    : attributeData[attribute.value].map((subAttr, i) => (
                        <div
                          key={`${subAttr}-${i}`}
                          className="fs-standard"
                          style={{
                            background: "#ddd",
                            borderRadius: "2px",
                            border: "1px solid #777",
                            color: "#777",
                            margin: "3px 5px 5px 0",
                            padding: "2px 12px",
                          }}
                        >
                          {subAttr}
                        </div>
                      ))
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </Col>
        <Col xs={12} style={{ paddingTop: "2rem", paddingBottom: "3rem", marginTop: "1rem" }} hidden={review}>
          <ButtonGroup
            buttons={attributeDropDown}
            value={selectedAttribute}
            onSelect={(value) => {
              setSelectedAttribute(value);
            }}
          />
        </Col>
        <Col xs={12} style={{ marginTop: "-1rem" }} hidden={review}>
          {review ?
            <TagsReviewDisplay
              tags={attributeData[selectedAttribute]}
              toolTip={t(($) => $.advertising["Give us a list of the attributes that describe your products"])}
            />
          : <StringListInput
              aria-label={t(($) => $.advertising["Attributes"])}
              value={attributeData[selectedAttribute] ?? []}
              onChange={(v) => changeAttributeData(selectedAttribute, v)}
              placeholder={t(($) => $.advertising["Enter attributes..."])}
              isDisabled={(adId || review) && !isModal}
              bulkEditTitle={t(($) => $.advertising["Attributes"])}
            />
          }
          <div style={{ paddingRight: "2rem" }} hidden={review}>
            {selectedAttribute !== "other" && (
              <span style={{ fontSize: "12px" }}>
                {t(($) => $.advertising["Give us a list of your {attribute}s"], { attribute: selectedAttribute })}
              </span>
            )}
            {selectedAttribute === "other" && (
              <span style={{ fontSize: "12px" }}>
                {t(($) => $.advertising["Tell us about your generic attributes"])}
              </span>
            )}
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <div style={{ padding: "0 2rem" }}>
        <Row>
          <Col xs={12} md={12}>
            <Row>
              {showWarning && (
                <Col xs={12} md={12}>
                  <Alert
                    variant="warning"
                    style={{
                      backgroundColor: "#c43066",
                      color: "#ffffff",
                      fontWeight: 400,
                    }}
                  >
                    <p style={{ color: "#fff" }}>
                      <strong>{t(($) => $.advertising["Warning!"])}</strong>
                    </p>
                    <p style={{ color: "#fff" }}>
                      {t(
                        ($) =>
                          $.advertising[
                            "It looks like the keywords you are using to describe your product or your brand names contain one or more attribute name. We recommend that you describe your product using generic terms to generate better results."
                          ],
                      )}
                    </p>
                  </Alert>
                </Col>
              )}
              {state.canHaveChildren ? getCompetitorBrands() : getProductTypes()}
              <Col xs={12} lg={6} style={{ paddingLeft: "2rem" }}>
                <FieldWrapper>
                  <div style={{ overflow: "auto" }}>
                    {review ?
                      <TagsReviewDisplay
                        tags={state.filledProductBrands}
                        label={t(($) => $.advertising["Home Brand"])}
                        toolTip={t(
                          ($) => $.advertising["Tell us the brands that you are promoting in your media plan"],
                        )}
                      />
                    : <StringListInput
                        label={
                          <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                            <span>
                              {state.canHaveChildren ?
                                t(($) => $.advertising["Home Brand"])
                              : t(($) => $.advertising["Related Brands"])}
                            </span>
                            <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                              {t(($) => $.advertising["Tell us the brands that you are promoting in your media plan"])}
                            </HoverInfo>
                          </span>
                        }
                        value={state.filledProductBrands ?? []}
                        onChange={(b) => changeBrand(b)}
                        placeholder={
                          state.canHaveChildren ?
                            t(($) => $.advertising["Enter home brand..."])
                          : t(($) => $.advertising["Enter competitor brands..."])
                        }
                        isDisabled={(adId || review) && !isModal}
                        bulkEditTitle={
                          state.canHaveChildren ?
                            t(($) => $.advertising["Home Brand"])
                          : t(($) => $.advertising["Related Brands"])
                        }
                      />
                    }
                  </div>
                  <Row hidden={review}>
                    <div
                      style={{
                        paddingTop: 0,
                        fontSize: "12px",
                        paddingLeft: "1.6rem",
                      }}
                    >
                      <span>{t(($) => $.advertising["e.g. Polo, Thrive, xRacer"])}</span>
                    </div>
                    {errors.filledProductBrands && fieldsToValidate?.includes("filledProductBrands") && (
                      <small className="text-danger pt-2 ps-4">{errors.filledProductBrands}</small>
                    )}
                  </Row>
                </FieldWrapper>
              </Col>
            </Row>
          </Col>
          {state.canHaveChildren && getProductTypes()}
        </Row>
        {getAttributeTitle()}
      </div>
      <Modal show={show} onHide={handleClose} style={{ marginTop: "10vh" }}>
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={8}>{t(($) => $.advertising["Choose one or multiple keywords"])}</Col>
            <Col xs={4} className="text-end" style={{ padding: "10px" }}>
              <SearchInput
                onChange={(value) => setKeywordSearch(value)}
                placeholder={t(($) => $.advertising["Filter Keywords"])}
                css={{ width: "200px" }}
              />
            </Col>
          </Row>
          <div
            style={{
              maxHeight: "500px",
              overflowY: "scroll",
              overflowX: "hidden",
            }}
          >
            <Row>
              {filteredKeywords.map((kw, i) => (
                <Col xs={4} key={i} style={{ paddingTop: "10px" }}>
                  <LinkButton onPress={() => addProductKeyword(kw)} className={css({ marginBottom: "1rem" })}>
                    <span>{kw}</span>
                  </LinkButton>
                </Col>
              ))}
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onPress={handleClose}>
            {t(($) => $.common.actions.Close)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AttributeSelector;
